/* eslint-disable @typescript-eslint/naming-convention */
import { CapacitorHttp } from '@capacitor/core';

import { PermissionService } from '../services/permission.service';
import { apiKey, componentsDirectory, dataDirectory } from '../models';

import { DataUpdateRunner } from './data-update-runner';
import { environment } from 'src/environments/environment';

type DataResource = 'config' | 'translations' | 'components';

export class DataUpdater {
    permissionService: PermissionService;
    env: string;
    region: string;
    lang: string;

    private readonly bapiResources: DataResource[] = ['config', 'translations'];

    readonly endpointPaths: Record<DataResource, (region: string, lang: string) => string> = {
        config: () => `config/sites`,
        translations: (region: string, lang: string) =>
            `translations/${region}/${lang}`,
        components: (region: string) =>
            `${componentsDirectory}/component-data-content-${region}`,
    };

    constructor(
        permissionService: PermissionService,
        env: string,
        userRegion: string,
        lang: string
    ) {
        this.permissionService = permissionService;
        this.env = env;
        this.region = userRegion;
        this.lang = lang;
    }

    async update(): Promise<void[]> {
        await this.permissionService.checkAndRequestPermission();

        const dataTypes: DataResource[] = Object.keys(
            this.endpointPaths
        ) as DataResource[];

        const promises = dataTypes.map((dataType: DataResource) =>
            this.requestData(dataType)
        );

        return Promise.all(promises);
    }

    async getDataLastModifiedDates(): Promise<(Date | null)[]> {
        await this.permissionService.checkAndRequestPermission();

        const dataTypes: DataResource[] = Object.keys(
            this.endpointPaths
        ) as DataResource[];

        const promises = dataTypes.map((dataType: DataResource) =>
            this.requestDataHeaders(dataType).then((res) => (res ? new Date(res['Last-Modified']) : null))
        );

        return Promise.all(promises);
    }

    private isBapiResource(resource: DataResource): boolean {
        return this.bapiResources.includes(resource);
    }

    private async requestData(resource: DataResource): Promise<void> {
        const endpoint = this.getDataEndpoint(resource);
        const fileName = this.endpointPaths[resource](this.region, this.lang);
        const filePath = this.buildFilePath(fileName);
        const url = this.isBapiResource(resource)
            ? `${endpoint}/${fileName}`
            : `${endpoint}/${fileName}.json`;
        const headers = this.getRequestHeaders(resource);

        try {
            const result = await CapacitorHttp.get({
                url,
                responseType: 'json',
                headers,
            })
                .then((res) => res.data)
                .catch((e) => {
                    console.log('Http data request error : ', e);
                    throw e;
                });

            return DataUpdateRunner.writeFile(
                filePath,
                JSON.stringify(result),
                true
            );
        } catch (err) {
            console.log('Data update refresh error: ', err);
        }
    }

    private requestDataHeaders(resource: DataResource): Promise<Record<string, string> | null> {
        const endpoint = this.getDataEndpoint(resource);
        const fileName = this.endpointPaths[resource](this.region, this.lang);
        const url = this.isBapiResource(resource)
            ? `${endpoint}/${fileName}`
            : `${endpoint}/${fileName}.json`;

        return CapacitorHttp.request({
            method: 'HEAD',
            url,
            headers: this.getRequestHeaders(resource),
        })
            .then((res) => res.headers)
            .catch((e) => {
                console.log('Data update headers error : ', e);
                return null;
            });
    }

    private getRequestHeaders(resource: DataResource): Record<string, string> {
        if (this.isBapiResource(resource)) {
            return { 'x-api-key': apiKey };
        }

        return {
            'Cache-Control':
                'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
            Expires: '0',
        };
    }

    private buildFilePath = (fileName: string, fileType: 'json' = 'json') => {
        const dataPath = dataDirectory.local;
        return `${dataPath}/${fileName}.${fileType}`;
    };

    private getDataEndpoint(resource: DataResource): string {
        if (this.isBapiResource(resource)) {
            return `${environment.apiUrl}/matrix-experience-app`;
        }

        return `${dataDirectory.external}/${environment.bapiEnv}`;
    }
}

